import { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tag, ImageIcon, ShoppingCart, ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const etatConfig = {
  occasion: { label: "Occasion", color: "bg-amber-100 text-amber-800" },
  reconditionne: { label: "Reconditionné", color: "bg-green-100 text-green-800" },
  comme_neuf: { label: "Comme neuf", color: "bg-teal-100 text-teal-800" },
};

const ClientPortalBonnesAffaires = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await API.get("/portal/bonnes-affaires");
        setProducts(res.data);
      } catch (error) {
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleBuy = async (product) => {
    setBuying(product.id);
    try {
      const res = await API.post(`/portal/products/${product.id}/buy`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'achat";
      toast.error(msg);
      setBuying(null);
    }
  };

  const openDetail = (product) => {
    setSelectedProduct(product);
    setCurrentPhotoIdx(0);
  };

  const closeDetail = () => {
    setSelectedProduct(null);
    setCurrentPhotoIdx(0);
  };

  const nextPhoto = () => {
    if (selectedProduct) {
      const photos = selectedProduct.photos || [];
      setCurrentPhotoIdx((prev) => (prev + 1) % photos.length);
    }
  };

  const prevPhoto = () => {
    if (selectedProduct) {
      const photos = selectedProduct.photos || [];
      setCurrentPhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-portal-bonnes-affaires">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Bonnes affaires</h1>
        <p className="text-muted-foreground">Produits d'occasion, reconditionnés et comme neufs</p>
      </div>

      {products.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => {
            const etat = etatConfig[product.etat] || etatConfig.occasion;
            const photos = product.photos || [];
            return (
              <Card
                key={product.id}
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                data-testid={`ba-product-${product.id}`}
                onClick={() => openDetail(product)}
              >
                <div className="relative h-48 bg-secondary/30 flex items-center justify-center overflow-hidden">
                  {photos.length > 0 ? (
                    <img
                      src={`${BACKEND}${photos[0]}`}
                      alt={product.nom}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-16 w-16 text-muted-foreground/30" />
                  )}
                  <Badge className={`absolute top-3 left-3 ${etat.color}`}>{etat.label}</Badge>
                  {photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {photos.length} photos
                    </div>
                  )}
                </div>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-lg">{product.nom}</p>
                    {product.marque && <p className="text-sm text-muted-foreground">{product.marque}</p>}
                  </div>
                  {product.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-2xl font-bold text-primary">{product.prix_vente?.toFixed(2)} EUR</span>
                    <Button
                      size="sm"
                      data-testid={`buy-btn-${product.id}`}
                      disabled={buying === product.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuy(product);
                      }}
                    >
                      {buying === product.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <ShoppingCart className="h-4 w-4 mr-1" />
                      )}
                      Acheter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Tag className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="font-medium">Aucune bonne affaire disponible</p>
            <p className="text-sm text-muted-foreground">Revenez bientôt pour découvrir nos offres</p>
          </CardContent>
        </Card>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="product-detail-modal"
          onClick={closeDetail}
        >
          <div
            className="bg-background rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Photo gallery */}
            <div className="relative h-64 sm:h-80 bg-secondary/30 flex items-center justify-center">
              {(selectedProduct.photos || []).length > 0 ? (
                <>
                  <img
                    src={`${BACKEND}${selectedProduct.photos[currentPhotoIdx]}`}
                    alt={selectedProduct.nom}
                    className="w-full h-full object-contain"
                  />
                  {selectedProduct.photos.length > 1 && (
                    <>
                      <button
                        onClick={prevPhoto}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                        data-testid="photo-prev"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextPhoto}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                        data-testid="photo-next"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {selectedProduct.photos.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentPhotoIdx(idx)}
                            className={`w-2.5 h-2.5 rounded-full transition-colors ${
                              idx === currentPhotoIdx ? "bg-white" : "bg-white/50"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <ImageIcon className="h-20 w-20 text-muted-foreground/30" />
              )}
              <button
                onClick={closeDetail}
                className="absolute top-3 right-3 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70"
                data-testid="close-modal"
              >
                <X className="h-4 w-4" />
              </button>
              <Badge className={`absolute top-3 left-3 ${(etatConfig[selectedProduct.etat] || etatConfig.occasion).color}`}>
                {(etatConfig[selectedProduct.etat] || etatConfig.occasion).label}
              </Badge>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h2 className="text-xl font-bold">{selectedProduct.nom}</h2>
                {selectedProduct.marque && (
                  <p className="text-sm text-muted-foreground">{selectedProduct.marque}</p>
                )}
              </div>

              {selectedProduct.description && (
                <p className="text-sm text-muted-foreground">{selectedProduct.description}</p>
              )}

              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <p className="text-3xl font-bold text-primary">{selectedProduct.prix_vente?.toFixed(2)} EUR</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedProduct.quantite_stock > 1
                      ? `${selectedProduct.quantite_stock} en stock`
                      : "Dernier exemplaire !"}
                  </p>
                </div>
                <Button
                  size="lg"
                  data-testid="modal-buy-btn"
                  disabled={buying === selectedProduct.id}
                  onClick={() => handleBuy(selectedProduct)}
                >
                  {buying === selectedProduct.id ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <ShoppingCart className="h-5 w-5 mr-2" />
                  )}
                  Acheter maintenant
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalBonnesAffaires;
